import React, { useState, useEffect } from 'react';
import { ServiceCardData } from './data';
import { PopupButton } from 'react-calendly';
import { CONTACT_US_CALENDLY_URL } from '../../../utils/constant/constants';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
// Import icons for Additional Services section
import { FaCheckCircle } from 'react-icons/fa';
// Import feature icons from constants
import { featureIcons } from './constants';
// Import components
import { FeatureItem, HeroSection, PackageComparison } from './components';

// Custom styles for text shadow
const styles = {
	shadowText: {
		textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
	},
};

function ServiceSectionNew() {
	const [expandedCard, setExpandedCard] = useState(null);
	const location = useLocation();

	// Handle anchor scrolling when component mounts or hash changes
	useEffect(() => {
		if (location.hash) {
			// Use requestAnimationFrame to ensure DOM is ready and avoid conflicts
			const scrollToElement = () => {
				const element = document.getElementById(location.hash.substring(1));
				if (element) {
					element.scrollIntoView({
						behavior: 'auto', // Use 'auto' instead of 'smooth' for immediate positioning
						block: 'start',
					});
				}
			};

			// Use requestAnimationFrame for better timing
			requestAnimationFrame(() => {
				requestAnimationFrame(scrollToElement);
			});
		}
	}, [location.hash]);

	// Toggle function to show/hide content
	const toggleReadMore = (id) => {
		setExpandedCard(expandedCard === id ? null : id);
	};

	return (
		<div className='pt-[80px]'>
			<HeroSection />

			<div id='packages'>
				<PackageComparison />
			</div>

			{/* Additional Services Section with improved cards */}
			<div id='additional-services' className='w-full mx-[auto] bg-[#F1F1F1]'>
				<div className='max-w-[1180px] mx-auto w-full p-[20px]'>
					<h3 className='font-canela-trial text-[35px] sm:text-[35px] text-[28px] my-[20px] mb-[35px] font-bold leading-[42px] sm:leading-[42px] leading-[34px] text-center text-clientBlack'>
						Additional Services
					</h3>
					<div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-6 gap-4 w-full mx-auto'>
						{ServiceCardData.length > 0 &&
							ServiceCardData?.map((service, index) => {
								// Get description from service data
								const serviceDescription =
									service.description ||
									'Professional marketing services tailored to your needs.';

								return (
									<div
										key={index}
										className='overflow-hidden bg-white rounded-[13px] shadow-md hover:shadow-lg transition-shadow duration-300 border-2 border-gray-300'>
										<div className='relative'>
											<img
												src={service.image}
												alt={service.title}
												loading='lazy'
												className='w-full h-[200px] sm:h-[200px] h-[160px] object-cover m-0 rounded-t-[13px] rounded-b-none'
											/>
										</div>

										<div className='bg-[#3A3A3A] py-3 px-4 text-center h-[70px] flex items-center justify-center'>
											<h3 className='font-lejour text-[20px] sm:text-[20px] text-[18px] font-normal leading-[24px] sm:leading-[24px] leading-[22px] uppercase m-0 text-white'>
												{service.headingStart}
											</h3>
										</div>

										<div className='px-6 py-4'>
											<div className='mb-4 text-center'>
												<div className='bg-gray-100 py-2 px-3 rounded-lg inline-block mb-3'>
													<p className='font-glacial text-[28px] font-bold leading-[32px] m-0 text-[rgba(204,_29,_185,_0.8)]'>
														{service.headingMiddle}
														<span className='font-bold text-[#3A3A3A]'>
															/{service.headingEnd}
														</span>
													</p>
												</div>
												<p className='text-gray-600 text-[16px] min-h-[48px]'>
													{serviceDescription}
												</p>
											</div>

											<div className='mt-4 h-auto'>
												{expandedCard === service.id ? (
													<div className='bg-gray-50 p-4 rounded-lg'>
														<ul className='list-none p-0 m-0'>
															{service.listingPint.map((listItem, idx) => {
																// Check if this is a header item (ends with a colon)
																const isHeader =
																	listItem.includes('</b>:') ||
																	listItem.includes('</b>');
																// Check if this is a sub-item (starts with an emoji)
																const isSubItem =
																	listItem.startsWith('📍') ||
																	listItem.startsWith('🎯') ||
																	listItem.startsWith('👥') ||
																	listItem.startsWith('🎞️') ||
																	listItem.startsWith('📅') ||
																	listItem.startsWith('🚀') ||
																	listItem.startsWith('🔗') ||
																	listItem.startsWith('🎨') ||
																	listItem.startsWith('📬') ||
																	listItem.startsWith('📆') ||
																	listItem.startsWith('🗓️') ||
																	listItem.startsWith('💬') ||
																	listItem.startsWith('✅');

																return (
																	<li
																		className={`flex items-start mb-3 font-glacial text-[16px] font-normal leading-[23.84px] text-clientDarkCharcoal ${
																			isSubItem ? 'ml-6' : ''
																		} ${isHeader ? 'mt-2' : ''}`}
																		key={idx}>
																		{!isSubItem && (
																			<FaCheckCircle className='text-[rgba(204,_29,_185,_0.8)] mr-2 mt-1 flex-shrink-0' />
																		)}
																		<span
																			dangerouslySetInnerHTML={{
																				__html: listItem,
																			}}></span>
																	</li>
																);
															})}
														</ul>
														<div className='flex justify-center mt-4'>
															<button
																type='button'
																className='mt-4 px-6 py-2 text-[15px] font-bold leading-[15px] border border-[rgba(204,_29,_185,_0.8)] text-[rgba(204,_29,_185,_0.8)] rounded-[4px] hover:bg-[rgba(204,_29,_185,_0.1)] transition-colors'
																onClick={() => toggleReadMore(service.id)}
																aria-label='Show less details'
																tabIndex={0}>
																View Less
															</button>
														</div>
													</div>
												) : (
													<div>
														<div className='h-[80px] overflow-hidden bg-gray-50 p-4 rounded-lg'>
															<ul className='list-none p-0 m-0'>
																{service.listingPint
																	.slice(0, 2)
																	.map((listItem, idx) => {
																		// Check if this is a header item (ends with a colon)
																		const isHeader =
																			listItem.includes('</b>:') ||
																			listItem.includes('</b>');
																		// Check if this is a sub-item (starts with an emoji)
																		const isSubItem =
																			listItem.startsWith('📍') ||
																			listItem.startsWith('🎯') ||
																			listItem.startsWith('👥') ||
																			listItem.startsWith('🎞️') ||
																			listItem.startsWith('📅') ||
																			listItem.startsWith('🚀') ||
																			listItem.startsWith('🔗') ||
																			listItem.startsWith('🎨') ||
																			listItem.startsWith('📬') ||
																			listItem.startsWith('📆') ||
																			listItem.startsWith('🗓️') ||
																			listItem.startsWith('💬') ||
																			listItem.startsWith('✅');

																		return (
																			<li
																				className={`flex items-start mb-3 font-glacial text-[16px] font-normal leading-[23.84px] text-clientDarkCharcoal ${
																					isSubItem ? 'ml-6' : ''
																				} ${isHeader ? 'mt-2' : ''}`}
																				key={idx}>
																				{!isSubItem && (
																					<FaCheckCircle className='text-[rgba(204,_29,_185,_0.8)] mr-2 mt-1 flex-shrink-0' />
																				)}
																				<span
																					dangerouslySetInnerHTML={{
																						__html: listItem,
																					}}></span>
																			</li>
																		);
																	})}
															</ul>
														</div>
														<div className='flex justify-center mt-4'>
															<button
																type='button'
																className='mt-4 px-6 py-2 text-[15px] font-bold leading-[15px] bg-[rgba(204,_29,_185,_0.8)] text-white rounded-[4px] hover:bg-[rgba(204,_29,_185,_0.7)] transition-colors'
																onClick={() => toggleReadMore(service.id)}
																aria-label='Show more details'
																tabIndex={0}>
																View Details
															</button>
														</div>
													</div>
												)}
											</div>
										</div>
									</div>
								);
							})}
					</div>

					<div className='text-center my-16'>
						<PopupButton
							className='h-[60px] w-full max-w-[298px] bg-[rgba(204,_29,_185,_0.8)] hover:bg-[rgba(204,_29,_185,_0.7)] transition-colors text-[18px] font-bold leading-[25.2px] text-clientWhite rounded-[6px]'
							url={CONTACT_US_CALENDLY_URL}
							rootElement={document.getElementById('root')}
							text='Schedule a Free Consultation'
						/>
					</div>
				</div>
			</div>
		</div>
	);
}

export default ServiceSectionNew;
